<template>
  <div class="search-select" @focusout="onFocusOut">
    <label class="search-select__label" :for="inputId">{{ label }}</label>
    <div class="search-select__control">
      <input
        :id="inputId"
        ref="input"
        v-model="query"
        class="search-select__input"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="open"
        :aria-controls="inputId + '-listbox'"
        :aria-activedescendant="activeDescendant"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @focus="onFocus"
        @input="onInput"
        @keydown="onKeydown"
      />
      <button
        v-if="modelValue"
        type="button"
        class="search-select__clear"
        :disabled="disabled"
        aria-label="Auswahl löschen"
        @mousedown.prevent
        @click="clearSelection"
      >
        <i class="fa fa-xmark" aria-hidden="true"></i>
      </button>
    </div>
    <ul
      v-show="open"
      :id="inputId + '-listbox'"
      class="search-select__list"
      role="listbox"
    >
      <li
        v-for="(option, index) in filteredOptions"
        :key="option.code || option.name"
        :id="inputId + '-option-' + index"
        class="search-select__option"
        :class="{
          'search-select__option--active': index === activeIndex,
          'search-select__option--selected': isSelected(option)
        }"
        role="option"
        :aria-selected="modelValue && modelValue.code === option.code"
        @mousedown.prevent="selectOption(option)"
      >
        <span class="search-select__option-name">{{ displayValue(option) }}</span>
        <span class="search-select__pills">
          <template v-if="statusMode === 'routes'">
            <span
              v-if="option.routes"
              class="search-select__pill search-select__pill--success"
              >Rundgang konfiguriert</span
            >
            <span v-else class="search-select__pill">Kein Rundgang</span>
          </template>
          <template v-else>
            <template v-if="option.documents || option.routes">
              <span
                v-if="option.documents"
                class="search-select__pill search-select__pill--success"
                >Dokumente</span
              >
              <span
                v-if="option.routes"
                class="search-select__pill search-select__pill--success"
                >Rundgänge</span
              >
            </template>
            <span v-else class="search-select__pill">Nicht konfiguriert</span>
          </template>
        </span>
      </li>
      <li v-if="filteredOptions.length === 0" class="search-select__empty">
        Keine passenden Einträge gefunden.
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "AdminSearchSelect",
  props: {
    inputId: { type: String, required: true },
    label: { type: String, required: true },
    options: { type: Array, default: () => [] },
    modelValue: { type: Object, default: null },
    placeholder: { type: String, default: "Suchen und auswählen" },
    disabled: { type: Boolean, default: false },
    statusMode: { type: String, default: "features" }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      query: "",
      open: false,
      activeIndex: -1
    };
  },
  computed: {
    filteredOptions() {
      const selectedDisplay = this.modelValue ? this.displayValue(this.modelValue) : "";
      const term =
        this.modelValue && this.query === selectedDisplay
          ? ""
          : this.query.trim().toLowerCase();
      const sorted = this.options
        .filter((option) => option && (option.name || option.code))
        .slice()
        .sort((a, b) => {
          const configuredDifference =
            Number(this.isConfigured(b)) - Number(this.isConfigured(a));
          return (
            configuredDifference ||
            (a.name || "").localeCompare(b.name || "", "de")
          );
        });
      const matches = term
        ? sorted.filter((option) => {
            const name = (option.name || "").toLowerCase();
            const code = (option.code || "").toLowerCase();
            return name.includes(term) || code.includes(term);
          })
        : sorted;
      return matches.slice(0, 100);
    },
    activeDescendant() {
      if (!this.open || this.activeIndex < 0) return undefined;
      return this.inputId + "-option-" + this.activeIndex;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (!this.open) {
          this.query = value ? this.displayValue(value) : "";
        }
      }
    }
  },
  methods: {
    isConfigured(option) {
      return this.statusMode === "routes"
        ? Boolean(option.routes)
        : Boolean(option.documents || option.routes);
    },
    displayValue(option) {
      return option.name + (option.code ? " (" + option.code + ")" : "");
    },
    isSelected(option) {
      return Boolean(this.modelValue && this.modelValue.code === option.code);
    },
    onFocus() {
      if (this.disabled) return;
      this.open = true;
      if (this.modelValue) {
        this.$refs.input.select();
      }
    },
    onInput() {
      if (this.modelValue) {
        this.$emit("update:modelValue", null);
      }
      this.open = true;
      this.activeIndex = -1;
    },
    onKeydown(event) {
      if (this.disabled) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!this.open) {
          this.open = true;
          this.activeIndex = this.filteredOptions.length > 0 ? 0 : -1;
          return;
        }
        if (this.filteredOptions.length === 0) return;
        this.activeIndex = (this.activeIndex + 1) % this.filteredOptions.length;
        this.scrollActiveIntoView();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!this.open) {
          this.open = true;
          this.activeIndex = this.filteredOptions.length - 1;
          return;
        }
        if (this.filteredOptions.length === 0) return;
        this.activeIndex =
          (this.activeIndex - 1 + this.filteredOptions.length) %
          this.filteredOptions.length;
        this.scrollActiveIntoView();
      } else if (event.key === "Enter") {
        if (
          this.open &&
          this.activeIndex >= 0 &&
          this.filteredOptions[this.activeIndex]
        ) {
          event.preventDefault();
          this.selectOption(this.filteredOptions[this.activeIndex]);
        }
      } else if (event.key === "Escape") {
        if (this.open) {
          event.preventDefault();
          this.closeList();
        }
      }
    },
    onFocusOut(event) {
      if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
        this.closeList();
      }
    },
    selectOption(option) {
      this.$emit("update:modelValue", option);
      this.closeList();
    },
    clearSelection() {
      this.$emit("update:modelValue", null);
      this.query = "";
      this.$refs.input.focus();
    },
    closeList() {
      this.open = false;
      this.activeIndex = -1;
      this.query = this.modelValue ? this.displayValue(this.modelValue) : "";
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        const element = document.getElementById(
          this.inputId + "-option-" + this.activeIndex
        );
        if (element) element.scrollIntoView({ block: "nearest" });
      });
    }
  }
};
</script>

<style scoped>
.search-select {
  position: relative;
  min-width: 0;
}

.search-select__label {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--admin-muted);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.search-select__control {
  position: relative;
}

.search-select__input {
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--admin-line-strong);
  border-radius: 0.55rem;
  background: var(--admin-surface);
  color: var(--admin-ink);
  padding: 0.55rem 2.4rem 0.55rem 0.85rem;
  font-size: 0.95rem;
  text-overflow: ellipsis;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}

.search-select__input:focus {
  border-color: var(--admin-ink);
  outline: 3px solid rgba(254, 237, 1, 0.45);
  outline-offset: 1px;
}

.search-select__input:disabled {
  background: var(--admin-background);
  color: var(--admin-muted);
  cursor: not-allowed;
}

.search-select__clear {
  position: absolute;
  top: 50%;
  right: 0.35rem;
  display: inline-flex;
  width: 2rem;
  min-height: 2rem;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 0.45rem;
  background: transparent;
  color: var(--admin-muted);
  padding: 0;
  transform: translateY(-50%);
}

.search-select__clear:hover:not(:disabled) {
  background: #edf0f4;
  color: var(--admin-ink);
}

.search-select__list {
  position: absolute;
  z-index: 40;
  top: calc(100% + 0.3rem);
  right: 0;
  left: 0;
  max-height: 17rem;
  margin: 0;
  overflow-y: auto;
  border: 1px solid var(--admin-line);
  border-radius: 0.65rem;
  background: var(--admin-surface);
  padding: 0.3rem;
  box-shadow: 0 12px 30px rgba(23, 26, 31, 0.16);
  list-style: none;
}

.search-select__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  border-radius: 0.45rem;
  padding: 0.55rem 0.65rem;
  cursor: pointer;
}

.search-select__option--active {
  background: #f1f4f9;
}

.search-select__option--selected {
  background: rgba(254, 237, 1, 0.25);
}

.search-select__option--selected.search-select__option--active {
  background: rgba(254, 237, 1, 0.4);
}

.search-select__option-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 650;
}

.search-select__pills {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.3rem;
}

.search-select__pill {
  border: 1px solid var(--admin-line);
  border-radius: 999px;
  background: var(--admin-background);
  color: var(--admin-muted);
  padding: 0.12rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.search-select__pill--success {
  border-color: #9cc79c;
  background: #e5f3e5;
  color: #2f6b2f;
}

.search-select__empty {
  padding: 0.75rem 0.65rem;
  color: var(--admin-muted);
  font-size: 0.9rem;
}

@media (max-width: 640px) {
  .search-select__option {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-select__pills {
    justify-content: flex-start;
  }
}
</style>
